'use client'

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@racefront/shadcn-ui'
import 'cropperjs/dist/cropper.css'
import { Upload, User } from 'lucide-react'
import { useRef, useState, ReactNode } from 'react'
import { Cropper, ReactCropperElement } from 'react-cropper'
import { useDropzone } from 'react-dropzone'

type AvatarCropperProps = {
  currentAvatar?: string
  fallback?: ReactNode
  alt?: string
  size?: number
  outputSize?: number
  onCrop: (file: File, dataUrl: string) => void
  labels?: Partial<typeof DEFAULT_LABELS>
  disabled?: boolean
}

const DEFAULT_LABELS = {
  clickToUpload: 'Click to upload',
  cropImage: 'Crop Image',
  adjustImage: 'Adjust the image to fit the desired area.',
  cancel: 'Cancel',
  save: 'Save',
}

export function AvatarCropper({
  currentAvatar,
  fallback,
  alt = 'Avatar',
  size = 96,
  outputSize = 100,
  onCrop,
  labels: customLabels,
  disabled = false,
}: AvatarCropperProps) {
  const labels = { ...DEFAULT_LABELS, ...customLabels }
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [previewUrl, setPreviewUrl] = useState(currentAvatar)
  const [tempImageUrl, setTempImageUrl] = useState('')
  const cropperRef = useRef<ReactCropperElement>(null)

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: ([file]) => {
      if (file) {
        setTempImageUrl(URL.createObjectURL(file))
        setIsDialogOpen(true)
      }
    },
    multiple: false,
    accept: { 'image/*': [] },
    disabled,
  })

  const handleClose = () => {
    setIsDialogOpen(false)
    if (tempImageUrl) {
      URL.revokeObjectURL(tempImageUrl)
      setTempImageUrl('')
    }
  }

  const handleSave = () => {
    const cropper = cropperRef.current?.cropper
    if (!cropper) return

    const canvas = cropper.getCroppedCanvas({
      width: outputSize,
      height: outputSize,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    })

    const dataUrl = canvas.toDataURL('image/jpeg', 0.9)
    canvas.toBlob(
      (blob) => {
        if (!blob) return
        const file = new File([blob], `avatar-${Date.now()}.jpg`, { type: 'image/jpeg' })
        setPreviewUrl(dataUrl)
        onCrop(file, dataUrl)
        handleClose()
      },
      'image/jpeg',
      0.9,
    )
  }

  return (
    <>
      <div {...getRootProps()} className={`group cursor-pointer ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}>
        <input {...getInputProps()} />
        <Avatar className="transition-opacity group-hover:opacity-80" style={{ width: size, height: size }}>
          <AvatarImage src={previewUrl} alt={alt} />
          <AvatarFallback className="bg-muted text-2xl">
            {fallback || <User style={{ width: size / 2, height: size / 2 }} />}
          </AvatarFallback>
        </Avatar>
        <div className="text-muted-foreground group-hover:text-foreground mt-2 flex items-center justify-center gap-1 text-xs transition-colors">
          <Upload className="h-3 w-3" />
          <span>{labels.clickToUpload}</span>
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{labels.cropImage}</DialogTitle>
            <DialogDescription>{labels.adjustImage}</DialogDescription>
          </DialogHeader>
          <div className="relative aspect-square w-full overflow-hidden rounded-lg">
            <Cropper
              className="h-full w-full"
              aspectRatio={1}
              src={tempImageUrl}
              viewMode={1}
              guides
              background={false}
              autoCropArea={1}
              ref={cropperRef}
            />
          </div>
          <DialogFooter className="flex flex-row gap-2 sm:justify-end">
            <Button onClick={handleClose} type="button" variant="outline">
              {labels.cancel}
            </Button>
            <Button type="button" onClick={handleSave}>
              {labels.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
